import { useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import styled from 'styled-components'

import { usePostResultPayment } from '~/features/payment/queries/usePayment'
import ParticipationBackground from '~/shared/assets/background/home-participation.png'
import ParticipationMobileBackground from '~/shared/assets/background/home-participation-mobile.png'
import Verified from '~/shared/assets/icons/Verified'
import herne3 from '~/shared/assets/images/herne3.png'
import Button from '~/shared/components/buttons/Button'
import useViewport from '~/shared/hooks/useViewport'
import { mypageTabState } from '~/shared/states'
import theme from '~/shared/styles/theme'

export default function Success() {
  const navigate = useNavigate()
  const viewport = useViewport()

  const { enqueueSnackbar } = useSnackbar()

  const { mutate: postResultPaymentMutate } = usePostResultPayment()

  const [_mypageTab, setMypageTab] = useRecoilState(mypageTabState)

  const [isConfirmed, setIsConfirmed] = useState(true)
  const [searchParams] = useSearchParams()
  const paymentKey = searchParams.get('paymentKey')
  const paymentType = searchParams.get('paymentType')
  const orderId = searchParams.get('orderId')
  const amount = searchParams.get('amount')

  const redirectToMypage = () => {
    setMypageTab(1)
    navigate('/mypage')
  }

  useEffect(() => {
    if (paymentKey && orderId && amount) {
      postResultPaymentMutate(
        {
          paymentInfo: {
            success: true,
            order_code: orderId,
            payment_key: paymentKey,
            amount: Number(amount),
            payment_type: paymentType || undefined,
          },
        },
        {
          onSuccess: (res) => {
            if (res.errorCode !== 0) {
              enqueueSnackbar(res.errorMsg, { variant: 'error' })

              return navigate(`/fail?message=${res.errorMsg}`)
            }
            setIsConfirmed(true)
          },
          onError: (error) => {
            enqueueSnackbar(error.message, { variant: 'error' })

            return navigate('/fail')
          },
        },
      )
    }
  }, [paymentKey, orderId, amount])

  return (
    <Wrapper>
      {isConfirmed ? (
        <>
          <Container>
            <MainBox>
              <Title>결제 완료</Title>
              <VerifiedIcon />
            </MainBox>
            <SubText>결제가 완료되었습니다.</SubText>
            <Button
              $width={224}
              $height={58}
              $fontSize={24}
              $variant="transparent"
              onClick={redirectToMypage}
            >
              내 결제 정보 확인하기
            </Button>
            <BackgroundCharacter
              className="character"
              src={herne3}
              alt=""
              width={''}
              height={''}
            />
          </Container>
          <Background
            src={
              viewport < theme.deviceSizes.medium
                ? ParticipationMobileBackground
                : ParticipationBackground
            }
            alt="participation-background"
          />
        </>
      ) : (
        <ConfirmContainer>
          <img
            src="https:static.toss.im/lotties/loading-spot-apng.png"
            width="120"
            height="120"
          />
          <Title>결제 처리 중이예요</Title>
        </ConfirmContainer>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.section`
  width: 100vw;
  min-height: calc(
    100vh - ${theme.size.headerHeight + theme.size.footerHeight}px
  );
  padding-top: ${theme.size.headerHeight}px;

  @media ${theme.device.medium} {
    height: auto;
    min-height: 700px;
    padding-top: ${theme.size.mobileHeaderHeight}px;
  }
`

const Container = styled.p`
  position: absolute;
  top: 40%;
  left: 30%;
  transform: translate(-50%, -50%);

  display: flex;
  flex-direction: column;
  gap: 36px;

  width: 50%;
  margin: auto;

  .character {
    position: absolute;
    z-index: -1;
    top: -100px;
    right: -70%;

    @media ${theme.device.medium} {
      right: 0;
    }
  }

  @media ${theme.device.medium} {
    position: relative;
    position: absolute;
    z-index: 99;
    top: 20%;
    left: 50%;
    transform: translate(-50%, -50%);

    gap: 24px;

    width: 90%;
  }
`

const Background = styled.img`
  position: absolute;
  z-index: ${theme.zIndex.belowDefault};
  top: 0;

  width: 100%;
  height: 100%;

  object-fit: cover;
`

const MainBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
`

const Title = styled.h1`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 60px;
  font-weight: 500;
  line-height: 84px;
  text-align: left;
  letter-spacing: -0.02em;

  @media ${theme.device.medium} {
    font-size: 36px;
  }
`

const VerifiedIcon = styled(Verified)`
  @media ${theme.device.medium} {
    width: 40px;
    height: 40px;
    font-size: 36px;
  }
`

const SubText = styled.div`
  font-family: NanumSquareNeo-Regular, sans-serif;
  font-size: 32px;

  @media ${theme.device.medium} {
    font-size: 21px;
  }
`

const ConfirmContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;

  width: 100%;
  max-width: 540px;
  height: 400px;
  margin-top: 72px;
`

const BackgroundCharacter = styled.img`
  position: absolute;
  top: -100px;
  right: -70%;
  width: 600px;

  @media ${theme.device.medium} {
    z-index: -1;
    top: -200px;
    right: 0;
    width: 80%;
  }
`
